import { useState } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const SERVICES_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Travel Services Pakistan – flightfare.pk",
    "url": `${BASE_URL}/services`,
    "description": "flightfare.pk offers global airline ticketing services and professional air ticketing courses in Pakistan. IATA-accredited travel services from Lahore."
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Travel Services by flightfare.pk",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Course",
          "name": "Travel Agency Air Ticketing Course",
          "description": "Professional air ticketing course covering GDS systems – Sabre, Galileo, Amadeus & web ticketing, basic visa information and IATA knowledge.",
          "provider": { "@type": "Organization", "name": "flightfare.pk" },
          "offers": {
            "@type": "Offer",
            "price": "25000",
            "priceCurrency": "PKR",
            "availability": "https://schema.org/InStock"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Global Airline Ticketing Services in Pakistan",
          "description": "flightfare.pk offers global airline ticketing services in Pakistan for international airlines without local ticketing offices.",
          "provider": { "@type": "Organization", "name": "flightfare.pk" },
          "areaServed": "PK",
          "serviceType": "Airline Ticketing"
        }
      }
    ]
  }
];

const SERVICES = [
  {
    image: "https://public.readdy.ai/ai/img_res/5f5f6509b3cf32a613ef17d4fd1961ec.jpg",
    title: "Travel Agency Air Ticketing Course",
    subtitle: "Book tickets on GDS — Sabre, Galileo, Amadeus & Web Ticketing. Basic visa information and IATA knowledge included.",
    price: "25,000",
    tag: "COURSE",
    tagColor: "bg-amber-500",
    highlights: ["Book Tickets on GDS", "Sabre, Galileo, Amadeus & Web Ticketing", "Basic Visa Information", "IATA Information"],
  },
  {
    image: "https://readdy.ai/api/search-image?query=global%20airline%20ticketing%20Pakistan%20travel%20agent%20booking%20international%20flights%20computer%20screen%20professional%20office%20modern%20workspace%20aviation%20industry&width=400&height=260&seq=services-ticketing-001&orientation=landscape",
    title: "Global Airline Ticketing Services in Pakistan",
    subtitle: "flightfare.pk offers global airline ticketing services in Pakistan for airlines without local ticketing offices.",
    price: null,
    tag: "SERVICE",
    tagColor: "bg-cyan-600",
    highlights: ["International Airlines", "Competitive Fares", "24/7 Support", "Instant Confirmation"],
  },
];

interface InquiryModalProps {
  serviceName: string;
  onClose: () => void;
}

function InquiryModal({ serviceName, onClose }: InquiryModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name) data.append(el.name, el.value);
    }
    try {
      await fetch("https://readdy.ai/api/form/d725kqrk1jkj467r5s6g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-cyan-600 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-white text-xs font-medium opacity-80">Enquiry for</p>
            <h2 className="text-white font-bold text-sm leading-snug">{serviceName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mb-4">
                <i className="ri-checkbox-circle-line text-green-500 text-3xl"></i>
              </div>
              <h3 className="text-gray-900 font-bold text-lg mb-2">Enquiry Submitted!</h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                Thank you! Our team will get back to you shortly regarding your enquiry.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="service" value={serviceName} />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="+92 300 0000000"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  name="city"
                  placeholder="e.g. Karachi, Lahore, Islamabad"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Message</label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Any specific questions or requirements..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-0.5">{charCount}/500</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {loading ? "Submitting..." : "SUBMIT ENQUIRY"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const [activeService, setActiveService] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SeoMeta
        title="Travel Services Pakistan – Airline Ticketing & Travel Courses | flightfare.pk"
        description="Book global airline tickets and enrol in our professional air ticketing course in Pakistan. flightfare.pk provides IATA-accredited travel services and GDS training from Lahore."
        keywords="global airline ticketing Pakistan, air ticketing course Pakistan, IATA course Lahore, travel agent course Pakistan, international airline tickets Lahore"
        canonical="/services"
        jsonLd={SERVICES_JSON_LD}
      />

      {/* Inquiry Modal */}
      {activeService && (
        <InquiryModal
          serviceName={activeService}
          onClose={() => setActiveService(null)}
        />
      )}

      {/* Hero Banner */}
      <div className="pt-16">
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 40%, #22d3ee 70%, #a3e8ef 100%)" }}>
          <div className="absolute top-0 left-0 right-0 h-2 bg-amber-400" />
          <div className="absolute top-6 right-24 w-10 h-10 rounded-full border-2 border-white/30" />
          <div className="absolute top-16 right-64 w-5 h-5 rounded-full border border-white/20" />
          <div className="absolute bottom-8 left-16 w-8 h-8 rounded-full border border-white/20" />
          <div className="absolute bottom-16 left-48 w-4 h-4 rounded-full border border-white/30" />

          <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h1 className="text-3xl font-extrabold text-white leading-tight tracking-wide uppercase">
                Our Exclusive Travel Service
              </h1>
              <p className="text-white/90 text-sm mt-3 leading-relaxed">
                Discover flightfare.pk&apos;s comprehensive services including global airline ticketing and professional air ticketing courses.
              </p>
            </div>
            <div className="hidden md:block flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20customer%20service%20agent%20headset%20computer%20travel%20booking%20support%20desk%20modern%20office%20workspace%20illustration%20cartoon%20style%20teal%20background&width=300&height=260&seq=services-hero-001&orientation=portrait"
                alt="Our Services"
                className="w-64 h-52 object-contain object-top"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Offered Services */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Offered Services</h2>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-6">
            {SERVICES.map((service) => (
              <div
                key={service.title}
                className="w-full sm:w-80 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all group cursor-pointer"
              >
                <div className="w-full h-52 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className={`absolute top-3 left-3 px-3 py-1 ${service.tagColor} text-white text-xs font-bold rounded-full uppercase tracking-wide`}>
                    {service.tag}
                  </span>
                </div>
                <div className="p-5">
                  <p className="text-sm font-bold text-gray-900 mb-2">{service.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{service.subtitle}</p>
                  <ul className="space-y-1 mb-4">
                    {service.highlights.map(h => (
                      <li key={h} className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>
                  {service.price && (
                    <p className="text-sm font-semibold text-gray-900">
                      From PKR <span className="text-amber-600 font-bold">{service.price}</span>
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveService(service.title)}
                    className="mt-3 w-full py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    ENQUIRE NOW
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            flightfare.pk offers a wide range of comprehensive travel services, including ticketing on global airlines, ensuring you have access to the best flight options for your journey. Our expert team is dedicated to finding you the most convenient and cost-effective flights, tailored to your specific travel needs. Whether you are planning a business trip, family vacation, or solo adventure, we work with leading airlines worldwide to provide you with seamless and hassle-free travel experiences.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            In addition to our top-notch ticketing services, flightfare.pk is proud to offer a professional air ticketing course designed to equip aspiring travel professionals with the necessary skills and knowledge. Our comprehensive course covers all aspects of air ticketing, including reservation systems, fare calculations, and customer service. Taught by industry experts, this course is perfect for those looking to start a career in the travel industry or enhance their existing skills.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Choose flightfare.pk for all your travel needs and professional development. Our commitment to excellence and customer satisfaction ensures that you receive the highest quality services and support. Whether you are booking a flight or enrolling in our air ticketing course, you can trust us to provide you with the expertise and resources you need. Explore the world with confidence and build a rewarding career in travel with flightfare.pk.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
